import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PublishIcon from "@mui/icons-material/Publish";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { Button, Dialog, IconButton } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  selectReferenceLanguage,
  selectTrainingName,
} from "../../app/slices/TrainingSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { PublishStep, usePublishCourse } from "../../hooks/PublishCourseHook";
import PublishSuccessCard from "./PublishSuccessCard";
import "./PublishDialog.css";
import CourseTranslationRow from "./CourseTranslationRow";
import { countNeedingAttention } from "./courseTranslationStatus";
import PiButton from "../../components/PiButton";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import PiInputfield from "../../components/PiInputField";
import PublishErrorCard from "./PublishErrorCard";
import {
  PUBLISH_UNKNOWN_CODE,
  PublishFailure,
  toneForCode,
} from "./publishErrors";

interface PublishDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function PublishDialog(props: PublishDialogProps) {
  const { open, onClose } = { ...props };
  const trainingName = useSelector(selectTrainingName);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const {
    currentStep,
    getCourseDetails,
    translations,
    onChangeDto,
    estimatedTimeInSecondsInput,
    onChangeEstimatedTimeInSeconds,
    publishTraining,
    isInputInvalid,
    isEstimatedTimeInSecondsInvalid,
    translateAll,
    translateMissing,
    isLoading,
    hasMissingTranslations,
    publishFailure,
    retryPublish,
    publishedLanguages,
    estimatedTimeInSeconds,
  } = usePublishCourse();
  const refLang = useSelector(selectReferenceLanguage);
  const languagesNeedingAttention = countNeedingAttention(translations);
  const onOpenPortal = () => {
    window.open("https://portal.playitsafe.eu", "_blank", "noopener,noreferrer");
  };
  const height = "90vh";
  const width = "70vw";
  const { t } = useTranslation();
  const isDurationEmpty = estimatedTimeInSecondsInput.trim() === "";
  const durationErrorMessage = isDurationEmpty
    ? "*Duration is required."
    : "Duration must be a positive whole number.";

  useEffect(() => {
    if (open) {
      getCourseDetails();
    }
  }, [open]);

  const translationCards = translations.map((t) => {
    return (
      <CourseTranslationRow
        key={t.languageId}
        lang={t.languageId}
        shortcode={t.languageId}
        dto={t}
        onChange={onChangeDto}
        defaultOpen={t.languageId === refLang}
      />
    );
  });

  const loadingIndicator = (
    <div className="publishdialog-loading-indicator">
      <div>
        <CircularProgress color="inherit" size="100px" />
      </div>
    </div>
  );

  const fillInCourseDetails = (
    <>
      {isLoading && loadingIndicator}
      {!isLoading && (
        <div className="w-full h-full min-h-0 pt-[12px] flex flex-col gap-[24px]">
          {hasMissingTranslations && (
            <div className="flex items-center gap-[12px] px-[16px] py-[12px] bg-[#FFF8E1] rounded-[8px]">
              <WarningIcon
                sx={{ color: "#F57C00", fontSize: 24 }}
              />
              <span className="flex-1 text-[14px] text-[#F57C00]">
                {t("publish_page.missing_interaction_translations")}
              </span>
            </div>
          )}
          <section className="flex flex-col gap-[12px]">
            <h2 className="m-0 text-[15px] font-semibold text-[var(--pi-text-color-1)]">
              {t("publish_page.general_info", { defaultValue: "General info" })}
            </h2>
            <div className="w-[260px] max-w-full">
              <PiInputfield
                value={estimatedTimeInSecondsInput}
                label={t("publish_page.estimated_duration", {
                  defaultValue: "Duration (in seconds)",
                })}
                placeholder={t("publish_page.estimated_duration", {
                  defaultValue: "Duration (in seconds)",
                })}
                type="number"
                min={1}
                step={1}
                error={isEstimatedTimeInSecondsInvalid}
                errorMessage={durationErrorMessage}
                onChange={onChangeEstimatedTimeInSeconds}
              />
            </div>
          </section>
          <div className="h-px w-full bg-creator-border" />
          <section className="flex flex-col gap-[20px] min-h-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[8px]">
              <h2 className="m-0 text-[15px] font-semibold text-[var(--pi-text-color-1)]">
                {t("publish_page.course_translations", {
                  defaultValue: "Course translations",
                })}
              </h2>
              {/* Actions hug their labels so the row still fits in the longest
                  locale; flex-wrap drops them below the title if it ever does not. */}
              <div className="ml-auto flex items-center gap-[8px]">
                <div className="h-[32px] w-fit">
                  <PiButton
                    variant="piBlueOutlined"
                    onClick={() => translateMissing()}
                    disabled={false}
                  >
                    <div className="flex items-center justify-center gap-[6px] px-[12px] text-[18px]">
                      <TranslateOutlinedIcon
                        color="inherit"
                        fontSize="inherit"
                      />
                      <div className="whitespace-nowrap text-[12.5px]">
                        {t("localization_menu.translate_missing")}
                      </div>
                      {languagesNeedingAttention > 0 && (
                        <div className="rounded-full bg-[var(--pi-key-fade)] px-[6px] text-[10.5px] font-semibold leading-[16px]">
                          {languagesNeedingAttention}
                        </div>
                      )}
                    </div>
                  </PiButton>
                </div>
                <div className="h-[32px] w-fit">
                  <PiButton
                    variant="piBlueOutlined"
                    onClick={() => {
                      translateAll();
                    }}
                    disabled={false}
                  >
                    <div className="flex items-center justify-center gap-[6px] px-[12px] text-[18px]">
                      <TranslateOutlinedIcon
                        color="inherit"
                        fontSize="inherit"
                      />
                      <div className="whitespace-nowrap text-[12.5px]">
                        {t("localization_menu.translate_all")}
                      </div>
                    </div>
                  </PiButton>
                </div>
              </div>
            </div>
            <div className="overflow-y-auto overflow-x-hidden pit-creator-scrollbar h-full min-h-0 pr-[4px]">
              <div className="overflow-hidden rounded-[8px] border border-solid border-creator-border bg-creator-surface">
                {translationCards}
              </div>
            </div>
          </section>
          <div className="publishdialog-footer">
            {languagesNeedingAttention > 0 && (
              <div className="flex shrink-0 items-center gap-[7px]">
                <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[var(--pi-key-yellow)]" />
                <span className="whitespace-nowrap text-[12.5px] text-[var(--pi-key-yellow-text)]">
                  {languagesNeedingAttention === 1
                    ? t("publish_page.needs_attention_one", {
                        defaultValue: "1 language needs attention",
                      })
                    : t("publish_page.needs_attention_many", {
                        defaultValue: "{{count}} languages need attention",
                        count: languagesNeedingAttention,
                      })}
                </span>
              </div>
            )}
            <Button
              variant="piBlue"
              onClick={publishTraining}
              disabled={isInputInvalid || hasMissingTranslations}
            >
              {t("publish")}
            </Button>
          </div>
        </div>
      )}
    </>
  );

  const finishedCard = (
    <>
      <div className="flex h-full min-h-0 flex-col gap-[20px] overflow-y-auto overflow-x-hidden pit-creator-scrollbar pt-[24px] pr-[4px]">
        <div className="flex items-center gap-[16px]">
          <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full bg-[var(--pi-key-green-faded)] text-[28px] text-[var(--pi-key-green)]">
            <CheckIcon color="inherit" fontSize="inherit" />
          </div>
          <div className="flex min-w-0 flex-col gap-[4px]">
            <div className="text-22 font-semibold leading-[1.32] text-creator-text">
              {t("publish_window.publish_complete")}
            </div>
            <div className="text-[13px] leading-[1.5] text-creator-text-sub">
              {t("publish_window.complete_subtitle", {
                defaultValue:
                  "Your training is live.",
              })}
            </div>
          </div>
        </div>

        <PublishSuccessCard
          courseName={translations[0]?.name}
          languages={publishedLanguages}
          estimatedTimeInSeconds={estimatedTimeInSeconds}
        />
      </div>
      <div className="publishdialog-footer">
        <Button variant="piBlueOutlined" onClick={onClose}>
          {t("finish")}
        </Button>
        <div className="h-[36px] w-[150px]">
          <PiButton variant="piBlue" onClick={onOpenPortal}>
            <span>{t("publish_window.publish_portal")}</span>
          </PiButton>
        </div>
      </div>
    </>
  );

  const publishingIndicator = (
    <>
      <div className="publishdialog-loading-indicator">
        <div>
          <CircularProgress color="inherit" size="100px" />
        </div>
        <div className="publishdialog-loading-publish-icon">
          <PublishIcon fontSize="inherit" color="inherit" />
        </div>

        <div className="publishdialog-loading-publish-text">
          Publishing Course...
        </div>
      </div>
    </>
  );

  const failure: PublishFailure = publishFailure ?? {
    code: PUBLISH_UNKNOWN_CODE,
    message: "",
    status: -1,
  };
  const isFailureRetryable = toneForCode(failure.code) === "retryable";

  const publishingFailed = (
    <>
      <div className="flex h-full min-h-0 flex-col gap-[20px] overflow-y-auto overflow-x-hidden pit-creator-scrollbar pt-[24px] pr-[4px]">
        <div className="flex items-center gap-[16px]">
          <div
            className={
              isFailureRetryable
                ? "flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full text-[28px] bg-[var(--pi-key-yellow-faded)] text-[var(--pi-key-yellow)]"
                : "flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full text-[28px] bg-creator-error-washed text-creator-error"
            }
          >
            {isFailureRetryable ? (
              <WarningAmberOutlinedIcon color="inherit" fontSize="inherit" />
            ) : (
              <ErrorOutlineOutlinedIcon color="inherit" fontSize="inherit" />
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-[4px]">
            <div className="text-22 font-semibold leading-[1.32] text-creator-text">
              {t("publish_page.failed")}
            </div>
            <div className="text-[13px] leading-[1.5] text-creator-text-sub">
              {t("publish_page.failed_subtitle", {
                defaultValue:
                  "Nothing was published, so your course is unchanged. Fix the problem below and try again.",
              })}
            </div>
          </div>
        </div>

        <PublishErrorCard
          failure={failure}
          trainingName={trainingName}
          organizationName={currentOrganization?.name}
          organizationId={currentOrganization?.id}
        />

        <div className="text-[12px] leading-[1.5] text-creator-text-soft">
          {t("publish_page.support", {
            defaultValue:
              "If this keeps happening, use Copy details and send the message to",
          })}{" "}
          <a
            className="text-creator-text-key no-underline hover:underline"
            href="mailto:support@playit.training"
          >
            support@playit.training
          </a>
        </div>
      </div>
      <div className="publishdialog-footer">
        <Button variant="piBlueOutlined" onClick={onClose}>
          {t("publish_page.close", { defaultValue: "Close" })}
        </Button>
        <div className="h-[36px] w-[128px]">
          <PiButton
            variant="piBlue"
            onClick={retryPublish}
          >
            <span>{t("publish_page.try_again", { defaultValue: "Try again" })}</span>
          </PiButton>
        </div>
      </div>
    </>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case PublishStep.Initialize:
        return loadingIndicator;
      case PublishStep.EnterCourseDetails:
        return fillInCourseDetails;
      case PublishStep.Submitting:
        return publishingIndicator;
      case PublishStep.Finished:
        return finishedCard;
      case PublishStep.Failed:
        return publishingFailed;
    }
  };

  const dialog = (
    <Dialog
      open={open}
      maxWidth={"xs"}
      fullWidth
      PaperProps={{
        style: {
          minHeight: height,
          maxHeight: height,
          minWidth: width,
          maxWidth: width,
          borderRadius: "10px",
        },
      }}
    >
      <div className="publishdialog-closebutton">
        <IconButton onClick={() => onClose()}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="publishdialog-root">
        <div className="publishdialog-banner">
          <img src="/logo_verticaal.png" loading="lazy" />
        </div>
        <div className="publishdialog-body">
          <div className="publishdialog-content">
            <div className="publishdialog-title">
              <span>{t("publish")}</span>
              <div className="publishdialog-training-name">
                {" "}
                | {trainingName}
              </div>
            </div>
            {renderCurrentStep()}
          </div>
        </div>
      </div>
    </Dialog>
  );

  return <>{dialog}</>;
}
